import { Link } from 'react-router-dom';
import { Search, Gift, LayoutTemplate, Filter, Workflow, BarChart3 } from 'lucide-react';
import { PageShell, ServiceHero, ServiceLinks } from '../components/services/Shared';
import { WhatsAppCTA } from '../components/WhatsAppButton';
import { Reveal } from '../components/Reveal';
import { useLang } from '../i18n';

const STEPS = [
    { icon: Search },
    { icon: Gift },
    { icon: LayoutTemplate },
    { icon: Filter, flow: true },
    { icon: Workflow },
    { icon: BarChart3 },
];

export default function GrowthPartner() {
    const { t } = useLang();
    const FLOW = [0, 1, 2, 3, 4, 5].map((i) => t(`flow_${i}`));

    return (
        <PageShell titleKey="growth_meta_t" descKey="growth_meta_d" path="/servicios/growth-partner" waKey="wa_growth">
            <ServiceHero eyebrowKey="sp_name_growth-partner" h1Key="growth_h1" introKey="growth_intro" testid="growth-hero" />

            <section className="py-20 lg:py-24">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <Reveal>
                        <h2 className="font-display text-3xl lg:text-4xl font-semibold text-ink">{t('growth_modelo')}</h2>
                    </Reveal>

                    <div className="mt-12" data-testid="growth-funnel">
                        {STEPS.map((s, i) => (
                            <Reveal key={i} delay={i * 0.05}>
                                <div className="relative pl-16 sm:pl-20 pb-10 last:pb-0">
                                    {i < STEPS.length - 1 && (
                                        <span className="absolute left-[27px] sm:left-[31px] top-14 bottom-0 w-px bg-ink/15" aria-hidden="true" />
                                    )}
                                    <div className="absolute left-0 top-0 w-14 h-14 rounded-2xl bg-pastelpink text-ink flex items-center justify-center" data-testid={`growth-step-icon-${i + 1}`}>
                                        <s.icon size={22} strokeWidth={1.6} />
                                    </div>
                                    <h3 className="font-display text-xl sm:text-2xl font-semibold text-ink">
                                        {t(`g${i + 1}_t`)}
                                    </h3>
                                    <p className="mt-2 text-[15px] text-ink/70 leading-relaxed max-w-2xl">{t(`g${i + 1}_d`)}</p>
                                    {s.flow && (
                                        <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="growth-funnel-flow">
                                            {FLOW.map((f, j) => (
                                                <span key={f} className="flex items-center gap-2">
                                                    <span className="rounded-full bg-pastelblue border border-ink/10 px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.1em] text-ink">
                                                        {f}
                                                    </span>
                                                    {j < FLOW.length - 1 && <span className="text-crimson text-xs">→</span>}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="pb-20 lg:pb-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <Reveal>
                        <div className="rounded-[1.6rem] bg-ink text-pastelcream p-9 lg:p-12 relative overflow-hidden" data-testid="growth-mensaje">
                            <div className="absolute -left-14 -bottom-14 w-56 h-56 rounded-full bg-crimson/25 blur-3xl" />
                            <p className="font-ink text-[11px] uppercase tracking-[0.3em] text-pastelcream/60">{t('growth_msj_eyebrow')}</p>
                            <p className="font-display text-2xl lg:text-3xl leading-snug mt-5 relative">{t('growth_msj_main')}</p>
                            <p className="mt-5 text-pastelcream/75 leading-relaxed max-w-3xl relative">{t('growth_msj_sub')}</p>
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="pb-20 lg:pb-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <Reveal>
                        <div className="rounded-[1.6rem] border-2 border-crimson/30 bg-pastelpink/40 p-9 lg:p-14 text-center relative overflow-hidden" data-testid="growth-cta-final">
                            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-ink leading-tight">
                                {t('growth_cta_h')}
                            </h2>
                            <p className="mt-5 text-ink/70 max-w-2xl mx-auto">{t('growth_cta_sub')}</p>
                            <div className="mt-9 flex justify-center">
                                <WhatsAppCTA
                                    big
                                    label={t('wa_btn_nosotros')}
                                    message={t('wa_growth')}
                                    testid="growth-whatsapp-cta"
                                />
                            </div>
                            <p className="mt-6 text-sm text-ink/50">
                                {t('growth_cta_prefer')}
                                <Link to="/#agenda" className="text-crimson font-bold hover:underline">{t('growth_cta_link')}</Link>
                            </p>
                        </div>
                    </Reveal>
                </div>
            </section>

            <ServiceLinks currentId="growth-partner" />
        </PageShell>
    );
}
